<template>
    <div class="alert-lines-layer" :style="layerStyle">
        <canvas ref="canvas" class="alert-lines-canvas"></canvas>
        <div
            v-for="row in rows"
            :key="row.key"
            class="alert-line-hit"
            :style="{ top: row.y + 'px' }"
            @pointerdown.stop.prevent="startDrag(row.alert, $event)"
            @dblclick.stop.prevent="emitAction('edit', row.alert)"
            @contextmenu.stop.prevent="openMenu(row.alert, $event)"
        >
            <div class="alert-line-badge" :class="{ server: row.alert.source === 'server', disabled: row.alert.enabled === false }">
                {{ formatPrice(row.price) }}
            </div>
        </div>

        <button
            v-if="hover && !drag"
            type="button"
            class="alert-add-button"
            title="Add price alert"
            aria-label="Add price alert"
            :style="{ left: addButtonLeft + 'px', top: addButtonTop + 'px' }"
            @mouseenter="addButtonActive = true"
            @mouseleave="addButtonActive = false"
            @focus="addButtonActive = true"
            @blur="addButtonActive = false"
            @mousedown.stop.prevent
            @click.stop="emitAction('add', null, hover.price)"
        >+</button>

        <div
            v-if="menu.visible"
            class="alert-context-menu"
            :style="{ left: menu.x + 'px', top: menu.y + 'px' }"
            @click.stop
            @mousedown.stop
        >
            <button type="button" @click="editFromMenu">Edit alert</button>
            <button type="button" class="danger" @click="deleteFromMenu">Remove alert</button>
        </div>
    </div>
</template>

<script>
// Independent canvas+HTML overlay for price alerts. Deliberately does NOT
// go through the trading-vue-js DataCube/Overlay/Tool machinery: alerts are
// plain data from the alert API (see workspace/mixin.js workspaceAllAlerts),
// not persisted drawings, so every interaction here is relayed to the
// parent as a single 'alert-action' event and the existing alert CRUD
// pipeline (workspaceAction -> createAlert/editAlert/deleteAlert) stays
// the only place that talks to the server.
export default {
    name: 'AlertLinesLayer',
    props: {
        alerts: { type: Array, default: () => [] },
        // Function returning the live trading-vue-js layout object
        // ($refs.chart._layout) so this layer always reads fresh
        // price<->pixel conversion without relying on Vue reactivity
        // into a non-reactive $refs tree.
        getLayout: { type: Function, required: true },
        // Function returning the chart's own crosshair/cursor object
        // ($refs.chart.cursor) - already carries grid_id/y/y$ for
        // whichever grid is hovered, so the "+" button follows the
        // crosshair price at the price-axis edge.
        getCursor: { type: Function, required: true },
        width: { type: Number, default: 0 },
        height: { type: Number, default: 0 },
        gridId: { type: Number, default: 0 },
    },
    data() {
        return {
            rows: [],
            dragPrice: {},
            drag: null,
            menu: { visible: false, x: 0, y: 0, alert: null },
            hover: null,
            addButtonActive: false,
            raf: null,
        };
    },
    computed: {
        addButtonLeft() {
            const grid = this.currentGrid();
            const edge = grid && Number.isFinite(grid.width) ? grid.width : this.width;
            return Math.max(0, Math.min(edge - 26, this.width - 26));
        },
        addButtonTop() {
            if (!this.hover) return 0;
            return Math.min(Math.max(this.hover.y - 13, 2), this.height - 28);
        },
        layerStyle() {
            return {
                position: 'absolute',
                left: '0px',
                top: '0px',
                width: this.width + 'px',
                height: this.height + 'px',
                pointerEvents: 'none',
                zIndex: 45,
                overflow: 'hidden',
            };
        },
    },
    mounted() {
        window.addEventListener('pointermove', this.onWindowMouseMove);
        window.addEventListener('pointercancel', this.cancelDrag);
        window.addEventListener('blur', this.cancelDrag);
        window.addEventListener('pointerup', this.onWindowMouseUp);
        window.addEventListener('click', this.onWindowClick);
        this.loop();
    },
    beforeDestroy() {
        window.removeEventListener('pointermove', this.onWindowMouseMove);
        window.removeEventListener('pointercancel', this.cancelDrag);
        window.removeEventListener('blur', this.cancelDrag);
        window.removeEventListener('pointerup', this.onWindowMouseUp);
        window.removeEventListener('click', this.onWindowClick);
        if (this.raf) cancelAnimationFrame(this.raf);
    },
    methods: {
        loop() {
            this.tick();
            this.raf = requestAnimationFrame(this.loop);
        },
        tick() {
            const grid = this.currentGrid();
            if (!grid || typeof grid.$2screen !== 'function') {
                if (this.rows.length) this.rows = [];
                return;
            }
            const rows = [];
            for (const alert of this.alerts) {
                if (!alert || alert.drawing || !Number.isFinite(alert.target)) continue;
                const key = alert.source + ':' + alert.id;
                const price = this.dragPrice[key] != null ? this.dragPrice[key] : alert.target;
                const y = grid.$2screen(price);
                if (!Number.isFinite(y)) continue;
                rows.push({ alert, key, y, price });
            }
            this.rows = rows;
            // Entering the HTML button clears the underlying canvas cursor.
            // Keep its price and position stable until the pointer leaves it.
            if (!this.addButtonActive) this.hover = this.readHover(grid);
            this.draw(rows);
        },
        readHover(grid) {
            const cursor = this.getCursor();
            if (
                !cursor ||
                cursor.grid_id !== this.gridId ||
                !Number.isFinite(cursor.y) ||
                !Number.isFinite(cursor.y$)
            ) {
                return null;
            }
            // cursor.x/y are already in this grid's pixel space (see
            // components/js/updater.js CursorUpdater.sync).
            const x = Number.isFinite(cursor.x) ? cursor.x : this.width / 2;
            return { x, y: cursor.y, price: cursor.y$ };
        },
        currentGrid() {
            const layout = this.getLayout();
            return layout && layout.grids ? layout.grids[this.gridId] : null;
        },
        draw(rows) {
            const canvas = this.$refs.canvas;
            if (!canvas) return;
            if (canvas.width !== this.width || canvas.height !== this.height) {
                canvas.width = this.width;
                canvas.height = this.height;
            }
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, this.width, this.height);
            for (const row of rows) {
                const enabled = row.alert.enabled !== false;
                ctx.save();
                ctx.strokeStyle = enabled ? '#e91e63' : 'rgba(233, 30, 99, 0.45)';
                ctx.lineWidth = 1.5;
                ctx.setLineDash([6, 4]);
                ctx.beginPath();
                ctx.moveTo(0, row.y + 0.5);
                ctx.lineTo(this.width, row.y + 0.5);
                ctx.stroke();
                ctx.restore();
            }
        },
        formatPrice(value) {
            return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 6 });
        },
        emitAction(type, alert, price) {
            this.$emit('alert-action', { type, alert, price });
        },
        startDrag(alert, event) {
            if(event.button !== undefined && event.button !== 0)return;
            const key = alert.source + ':' + alert.id;
            this.drag = { alert, key, pointerId:event.pointerId, pointerType:event.pointerType, x:event.clientX, y:event.clientY, moved:false };
        },
        onWindowMouseMove(event) {
            if (!this.drag || event.pointerId !== this.drag.pointerId) return;
            if(Math.hypot(event.clientX-this.drag.x,event.clientY-this.drag.y)<4)return;
            this.drag.moved=true;
            const grid = this.currentGrid();
            if (!grid || typeof grid.screen2$ !== 'function') return;
            const rect = this.$el.getBoundingClientRect();
            const y = event.clientY - rect.top;
            const price = grid.screen2$(y);
            if (!Number.isFinite(price) || price <= 0) return;
            this.$set(this.dragPrice, this.drag.key, price);
        },
        onWindowMouseUp(event) {
            if (!this.drag || event.pointerId !== this.drag.pointerId) return;
            const { alert, key, pointerType, moved } = this.drag;
            const price = this.dragPrice[key];
            this.drag = null;
            if (Number.isFinite(price) && price > 0) {
                this.emitAction('drag', alert, price);
            }
            this.$delete(this.dragPrice, key);
            if(pointerType==='touch' && !moved)this.openMenu(alert,event);
        },
        cancelDrag() {
            if(this.drag)this.$delete(this.dragPrice,this.drag.key);
            this.drag=null;
        },
        openMenu(alert, event) {
            const rect = this.$el.getBoundingClientRect();
            this.menu = {
                visible: true,
                x: Math.min(event.clientX - rect.left, this.width - 140),
                y: Math.min(event.clientY - rect.top, this.height - 80),
                alert,
            };
        },
        closeMenu() {
            this.menu = { visible: false, x: 0, y: 0, alert: null };
        },
        editFromMenu() {
            const alert = this.menu.alert;
            this.closeMenu();
            if (alert) this.emitAction('edit', alert);
        },
        deleteFromMenu() {
            const alert = this.menu.alert;
            this.closeMenu();
            if (alert) this.emitAction('delete', alert);
        },
        onWindowClick(event) {
            // Touch pointerup opens the menu; its synthesized click must not close it.
            if(event?.target?.closest?.('.alert-line-hit'))return;
            if (this.menu.visible) this.closeMenu();
        },
    },
};
</script>

<style scoped>
.alert-lines-canvas {
    position: absolute;
    left: 0;
    top: 0;
    pointer-events: none;
}
.alert-line-hit {
    touch-action: none;
    position: absolute;
    left: 0;
    right: 56px;
    height: 14px;
    margin-top: -7px;
    pointer-events: auto;
    cursor: ns-resize;
}
.alert-line-badge {
    position: absolute;
    right: -56px;
    top: -9px;
    min-width: 52px;
    padding: 2px 6px;
    font-size: 11px;
    font-weight: 600;
    line-height: 14px;
    text-align: center;
    color: #fff;
    background: #e91e63;
    border-radius: 3px;
    pointer-events: auto;
    cursor: ns-resize;
    user-select: none;
    white-space: nowrap;
}
.alert-line-badge.server {
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.5) inset;
}
.alert-line-badge.disabled {
    opacity: 0.55;
}
.alert-add-button {
    position: absolute;
    width: 26px;
    height: 26px;
    border-radius: 2px;
    border: none;
    background: rgba(0, 0, 0, 0.65);
    color: #fff;
    font-size: 16px;
    line-height: 24px;
    text-align: center;
    padding: 0;
    cursor: pointer;
    pointer-events: auto;
    transition: background 0.15s ease, opacity 0.15s ease;
    z-index: 2;
    opacity: 1;
}
.alert-add-button:hover {
    background: rgba(0, 0, 0, 0.85);
    opacity: 1;
}
.alert-context-menu {
    position: absolute;
    z-index: 10000;
    background: #1e2228;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 6px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);
    padding: 4px;
    pointer-events: auto;
    display: flex;
    flex-direction: column;
    min-width: 130px;
}
.alert-context-menu button {
    background: transparent;
    border: none;
    color: #e6e6e6;
    text-align: left;
    padding: 6px 10px;
    font-size: 12px;
    border-radius: 4px;
    cursor: pointer;
}
.alert-context-menu button:hover {
    background: rgba(255, 255, 255, 0.08);
}
.alert-context-menu button.danger {
    color: #ff5a5a;
}
</style>
